import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  ModalCloseButton,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Stack,
  Text,
} from "@chakra-ui/react";

import { FiPlus } from "react-icons/fi";
import { useQuery } from "@tanstack/react-query";

import { axiosClient } from "../client/http-client";
import { getAuthToken } from "../client/token-utils";

import RegisterBranchApplicationModal from "./registerBranchApplicationModal";
import RequestCreditApplicationModal from "./requestCreditApplicationModal";

interface CreateApplicationModalProps {
  refetchApplications: () => void;
  /** Offer only one application type (the dashboard has branches, My Contracts has credit accounts); both when omitted. */
  kind?: "registerbranch" | "requestcredit";
  /**
   * Lets a page place the trigger elsewhere (e.g. the hero band actions slot).
   * Receives the modal's `onOpen`; when omitted the default "New application" button renders here.
   */
  renderTrigger?: (onOpen: () => void) => React.ReactNode;
  /** Optional lifted disclosure so a page can open the modal from more than one place. */
  disclosure?: Pick<ReturnType<typeof useDisclosure>, "isOpen" | "onOpen" | "onClose">;
}

const CreateApplicationModal: React.FC<CreateApplicationModalProps> = ({
  refetchApplications,
  kind,
  renderTrigger,
  disclosure,
}) => {
  const showRegisterBranch = kind !== "requestcredit";
  const showRequestCredit = kind !== "registerbranch";
  const internalDisclosure = useDisclosure();
  const { isOpen, onOpen, onClose } = disclosure ?? internalDisclosure;
  const {
    isOpen: registerBranchIsOpen,
    onOpen: registerBranchOnOpen,
    onClose: registerBranchOnClose,
  } = useDisclosure();

  const {
    isOpen: requestCreditIsOpen,
    onOpen: requestCreditOnOpen,
    onClose: requestCreditOnClose,
  } = useDisclosure();

  const token = getAuthToken();
  // Focus lands on the first choice card on open, not the close button (§11).
  const firstCardRef = useRef<HTMLButtonElement>(null);
  // The branch card is disabled while its application is active, so focus its "continue" link instead.
  const continueRef = useRef<HTMLButtonElement>(null);

  const [isRegisterBranchActive, setIsRegisterBranchActive] =
    useState<boolean>(false);
  const [isRequestCreditActive, setIsRequestCreditActive] =
    useState<boolean>(false);

  // get the active application
  const getRegisterBranchActiveApplication = async () => {
    const response = await axiosClient.get(
      "/applications/register-branch/load-saved-progress",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  };

  const {
    data: registerBranchApplicationData,
    isPending: isLoadingRegisterBranchData,
    refetch: refetchingRegisterBranchData,
  } = useQuery({
    queryKey: ["register-branch-active-application"],
    queryFn: getRegisterBranchActiveApplication,
    retry: false,
    enabled: showRegisterBranch,
  });

  // request credit data fetching
  const getRequestCreditActiveApplication = async () => {
    const response = await axiosClient.get(
      "/applications/request-credit/load-saved-progress",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  };

  const {
    data: requestCreditApplicationData,
    isPending: isLoadingRequestCreditData,
    refetch: refetchRequestCreditData,
  } = useQuery({
    queryKey: ["request-credit-active-application"],
    queryFn: getRequestCreditActiveApplication,
    retry: false,
    enabled: showRequestCredit,
  });


  useEffect(() => {
    registerBranchApplicationData
      ? setIsRegisterBranchActive(true)
      : setIsRegisterBranchActive(false);
    requestCreditApplicationData
      ? setIsRequestCreditActive(true)
      : setIsRequestCreditActive(false);
  }, [requestCreditApplicationData, registerBranchApplicationData]);

  return (
    <>
      {renderTrigger ? (
        renderTrigger(onOpen)
      ) : (
        <Button variant="primary" leftIcon={<FiPlus />} onClick={onOpen}>
          New application
        </Button>
      )}
      <Modal
        size={"xl"}
        isCentered
        onClose={onClose}
        isOpen={isOpen}
        motionPreset="slideInBottom"
        // While the branch lookup runs its card is disabled, so it cannot take focus: fall back to the first
        // enabled control in the body (data-autofocus-inside), e.g. the credit card or a "continue" link.
        initialFocusRef={
          !showRegisterBranch
            ? undefined
            : isRegisterBranchActive
            ? continueRef
            : isLoadingRegisterBranchData
            ? undefined
            : firstCardRef
        }
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text textStyle="label" color="text.muted" aria-hidden>
              NEW APPLICATION
            </Text>
            {/* ModalHeader renders a <header>; the h2 inherits its type so the dialog outline starts at h2. */}
            <Box as="h2">Start an application</Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody px={{ base: 5, md: 8 }} py={6} data-autofocus-inside>
            {showRegisterBranch && isRegisterBranchActive && (
              <Alert status="warning" mb={4}>
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle mr={2}>You have an unfinished branch application.</AlertTitle>
                  <AlertDescription>
                    <Button
                      ref={continueRef}
                      variant="link"
                      size="sm"
                      verticalAlign="baseline"
                      onClick={() => {
                        registerBranchOnOpen();
                      }}
                    >
                      Continue it
                    </Button>
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {showRequestCredit && isRequestCreditActive && (
              <Alert status="warning" mb={4}>
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle mr={2}>You have an unfinished credit account application.</AlertTitle>
                  <AlertDescription>
                    <Button
                      variant="link"
                      size="sm"
                      verticalAlign="baseline"
                      onClick={() => {
                        requestCreditOnOpen();
                      }}
                    >
                      Continue it
                    </Button>
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {/* The wizards render their own §7.4 choice-card triggers; this only stacks them. */}
            <Stack spacing={3} w="full">
              {showRegisterBranch && (
              <RegisterBranchApplicationModal
                triggerRef={firstCardRef}
                OnOpen={registerBranchOnOpen}
                isOpen={registerBranchIsOpen}
                onClose={registerBranchOnClose}
                applicationData={registerBranchApplicationData}
                refetchApplication={refetchingRegisterBranchData}
                refetchApplications={refetchApplications}
                lookingForActiveApplications={isLoadingRegisterBranchData}
                isApplicationActive={isRegisterBranchActive}
                setIsApplicationActive={setIsRegisterBranchActive}
              />
              )}
              {showRequestCredit && (
              <RequestCreditApplicationModal
                OnOpen={requestCreditOnOpen}
                isOpen={requestCreditIsOpen}
                onClose={requestCreditOnClose}
                applicationData={requestCreditApplicationData}
                refetchApplication={refetchRequestCreditData}
                lookingForApplications={isLoadingRequestCreditData}
                isApplicationActive={isRequestCreditActive}
                setIsApplicationActive={setIsRequestCreditActive}
                refetchApplications={refetchApplications}
              />
              )}
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreateApplicationModal;
